import { useCallback } from 'react'

interface ConfirmableActionOptions<T> {
  /**
   * The action to execute if confirmed
   */
  action: (param: T) => void

  /**
   * Confirmation message to show. Can be a string or a function that receives the parameter.
   */
  confirmMessage: string | ((param: T) => string)

  /**
   * Optional condition - if returns false, action executes without confirmation
   */
  shouldConfirm?: (param: T) => boolean
}

/**
 * Hook for wrapping any action with a confirmation dialog.
 *
 * @example
 * ```tsx
 * // Simple string message
 * const handleRemove = useConfirmableAction({
 *   action: (id) => removeMutation.mutate(id),
 *   confirmMessage: 'Remove this product from your collection?'
 * })
 *
 * // Dynamic message
 * const handleDelete = useConfirmableAction({
 *   action: (item) => deleteMutation.mutate(item.id),
 *   confirmMessage: (item) => `Delete ${item.name}?`
 * })
 *
 * // Conditional confirmation
 * const handleRemove = useConfirmableAction({
 *   action: (id) => removeMutation.mutate(id),
 *   confirmMessage: 'Remove this item?',
 *   shouldConfirm: (id) => quantities[id] > 10 // only confirm for large quantities
 * })
 * ```
 */
export function useConfirmableAction<T>(options: ConfirmableActionOptions<T>) {
  const { action, confirmMessage, shouldConfirm } = options

  return useCallback(
    (param: T) => {
      // If shouldConfirm is provided and returns false, execute without confirmation
      if (shouldConfirm && !shouldConfirm(param)) {
        action(param)
        return
      }

      // Get the message (could be string or function)
      const message = typeof confirmMessage === 'function' ? confirmMessage(param) : confirmMessage

      if (confirm(message)) {
        action(param)
      }
    },
    [action, confirmMessage, shouldConfirm],
  )
}
